// Core
import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

// Components
import { Button, FieldSelect, FieldText, SectionCard } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { openSuccessAlert, openNotificationPopup } from "state/application/applicationSlice";
import { setCommunity } from "state/communities/communitiesSlice";
import { useUpdateDebugOptionsMutation } from "state/communities/communitiesApiSlice";

const COMMUNITY_TYPE_OPTIONS = [
  { value: "PROD", label: "PROD" },
  { value: "DEMO", label: "DEMO" },
];

const MIN_TOLERANCE = 0.0;
const MAX_TOLERANCE = 1.0;

function DebugOptions({ data, isLoading }) {
  const [original, setOriginal] = useState(data);
  const [transactionAdjustmentTolerance, setTransactionAdjustmentTolerance] = useState(
    data.transaction_adjustment_tolerance
  );
  const [communityType, setCommunityType] = useState(data.type);

  const { t } = useTranslation(["community"]);
  const dispatch = useDispatch();
  const [updateDebugOptions] = useUpdateDebugOptionsMutation();

  const toleranceChanged =
    original.transaction_adjustment_tolerance !== transactionAdjustmentTolerance;
  const typeChanged = original.type !== communityType;
  const canSave = !isLoading && (toleranceChanged || typeChanged);

  const onToleranceChanged = (v) => {
    const value = Number(v);
    if (value < MIN_TOLERANCE) {
      setTransactionAdjustmentTolerance(MIN_TOLERANCE);
    } else if (value > MAX_TOLERANCE) {
      setTransactionAdjustmentTolerance(MAX_TOLERANCE);
    } else {
      setTransactionAdjustmentTolerance(value);
    }
  };

  const onUpdateClicked = async () => {
    if (!canSave) return;

    try {
      const result = await updateDebugOptions({
        communityId: data.id,
        type: communityType,
        transaction_adjustment_tolerance: transactionAdjustmentTolerance,
      }).unwrap();

      dispatch(setCommunity({ community: result }));
      dispatch(
        openSuccessAlert({
          title: t("basic_info.updated_title"),
          description: t("debug_options.updated_ok"),
        })
      );

      setOriginal({
        ...original,
        transaction_adjustment_tolerance: transactionAdjustmentTolerance,
        type: communityType,
      });
    } catch (err) {
      dispatch(
        openNotificationPopup({
          notification: err,
          type: "error",
        })
      );
    }
  };

  return (
    <SectionCard
      id="debug"
      icon="bug_report"
      title={t("debug_options.title")}
      action={
        <Button variant="ghost" size="sm" disabled={!canSave} onClick={onUpdateClicked}>
          <Icon name="save" />
          {t("debug_options.update")}
        </Button>
      }
    >
      <div
        className="flex items-center gap-2 text-[12.5px] font-semibold mb-[14px]"
        style={{
          padding: "10px 12px",
          background: "var(--warning-soft)",
          color: "var(--warning)",
          borderRadius: "var(--radius-sm)",
        }}
      >
        <Icon name="info" style={{ fontSize: 17 }} />
        {t("debug_options.admin_note")}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldText
          label={t("debug_options.transaction_adjustment_tolerance")}
          type="number"
          min={MIN_TOLERANCE}
          max={MAX_TOLERANCE}
          value={transactionAdjustmentTolerance}
          onChange={onToleranceChanged}
        />
        <FieldSelect
          label={t("debug_options.community_type")}
          value={communityType}
          options={COMMUNITY_TYPE_OPTIONS}
          onChange={setCommunityType}
        />
      </div>
    </SectionCard>
  );
}

DebugOptions.defaultProps = {
  isLoading: false,
};

DebugOptions.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    transaction_adjustment_tolerance: PropTypes.number,
  }).isRequired,
  isLoading: PropTypes.bool,
};

export default DebugOptions;
